import ms from 'ms';
const expiryTimeByAsset = {
    // Value based on https://solana.com/docs/advanced/confirmation#how-does-transaction-expiration-work.
    solana: ms('60s'),
};
const createTransactionSigner = ({ analytics, passkey, exchangeStatusAtom, addressProvider, }) => {
    const signTransaction = async ({ assetName, baseAssetName, unsignedTx, }) => {
        // EVM ACI supplies `baseAssetName` instead of `assetName`.
        // TODO: figure out if we can unify the parameter name.
        assetName = baseAssetName || assetName;
        const expiresAt = expiryTimeByAsset[assetName]
            ? Date.now() + expiryTimeByAsset[assetName]
            : undefined;
        const exchangeStatus = await exchangeStatusAtom?.get();
        const orderId = exchangeStatus?.status === 'started' ? exchangeStatus.orderId : undefined;
        const { address } = await addressProvider.getReceiveAddress({
            assetName,
        });
        const data = await passkey.signTransaction({
            baseAssetName: assetName,
            transaction: unsignedTx,
            expectedAddress: address,
            // This sets the time after which the Passkey Signer will block the transaction from being signed.
            ...(expiresAt && { expiresAt }),
            ...(orderId && { orderId }),
        });
        if (!data || data.error) {
            if (Date.now() >= expiresAt) {
                analytics?.track({
                    event: 'SignerTransactionExpiry',
                    properties: {
                        baseAssetName: assetName,
                    },
                });
            }
            throw new Error(data?.error ?? 'The signer rejected the request.');
        }
        return data;
    };
    const signTransactions = async (transactions) => {
        const firstTx = transactions[0];
        const assetName = firstTx.baseAssetName || firstTx.assetName;
        const { address } = await addressProvider.getReceiveAddress({
            assetName,
        });
        // This assumes that all transactions are for the same base asset.
        const expiresAt = expiryTimeByAsset[assetName]
            ? Date.now() + expiryTimeByAsset[assetName]
            : undefined;
        const transactionParams = transactions.map(({ baseAssetName, unsignedTx, ...rest }) => {
            const assetName = baseAssetName || rest.assetName;
            return {
                baseAssetName: assetName,
                transaction: unsignedTx,
                expectedAddress: address,
                // This sets the time after which the Passkey Signer will block the transaction from being signed.
                ...(expiresAt && { expiresAt }),
            };
        });
        const response = await passkey.signTransactions({
            transactions: transactionParams,
        });
        if (!response || response.error) {
            if (Date.now() >= expiresAt) {
                analytics.track({
                    event: 'SignerTransactionExpiry',
                    properties: {
                        baseAssetName: assetName,
                    },
                });
            }
            throw new Error(response?.error ?? 'The signer rejected the request.');
        }
        return response;
    };
    return {
        signTransaction,
        signTransactions,
    };
};
const transactionSignerDefinition = {
    id: 'transactionSigner',
    type: 'module',
    factory: createTransactionSigner,
    public: true,
    dependencies: [
        'analytics?',
        'addressProvider',
        'passkey',
        'exchangeStatusAtom?',
    ],
};
export default transactionSignerDefinition;
